import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setProgress } from "../../redux/slice/system";

import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

import { ModuleBlockTitle } from "../diagram/components";
import { option_colData } from "../../service/option/common";

import { classNames } from "primereact/utils";

const useChartPie = ({
  setting = {},
  isExport = true,
  isExportRawList = false,
  isFilter = false,
} = {}) => {
  const dispatch = useDispatch();

  const renderPie = useCallback(
    ({
      className = "",
      moduleTitle = "",
      targetId = "chart_pie",
      options,
    } = {}) => {
      return (
        <>
          <ModuleBlockTitle
            type="chart_pie"
            moduleTitle={moduleTitle}
            isExport={isExport}
            isExportRawList={isExportRawList}
            isFilter={isFilter}
            options={{
              setting,
              targetId,
              exportColumns: options.moduleData.exportColumns,
              tbData: options.chartData,
              callMainAPI: options.callMainAPI,
              progressAction: (flag) => dispatch(setProgress({ show: flag })),
            }}
          />
          <div
            className={classNames(
              "border-1 border-top-none border-400",
              className
            )}
          >
            <div id={targetId} className="h-full bg-white p-3">
              <ModulePie {...options} />
            </div>
          </div>
        </>
      );
    },
    []
  );

  return {
    renderPie,
  };
};

const ModulePie = ({
  moduleData = {},
  chartData = [],
  clickEvent = () => {},
}) => {
  const [showFilter, setShowFilter] = useState(null);
  const [finalData, setFinalData] = useState(null);

  useEffect(() => {
    if (!moduleData.columns) return;
    let temp = moduleData.columns?.reduce(
      (pre, cur) => ({ ...pre, [cur.key]: true }),
      {}
    );
    setShowFilter(temp);
  }, [chartData]);

  useEffect(() => {
    if (showFilter === null) return;

    let temp = chartData.format
      .filter((item) => showFilter[item[moduleData.levelColumns.key]])
      .map((item) => ({
        key: item[moduleData.levelColumns.key],
        label: item[moduleData.levelColumns.key],
        value: item[item[moduleData.levelColumns.key]],
      }));

    let total = temp.reduce((pre, cur) => (pre += cur.value), 0);
    temp = temp.map((item) => ({
      ...item,
      percent: Math.round((item.value * 10000) / total) / 100,
    }));
    setFinalData(temp);
  }, [showFilter]);

  const renderActiveShape = (props) => {
    const { cx, cy, midAngle, outerRadius, fill, label, value, percent } =
      props;
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 20) * cos;
    const my = cy + (outerRadius + 20) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text
          x={ex + (cos >= 0 ? 0.7 : -0.7) * 12}
          y={ey + 5}
          textAnchor={textAnchor}
          // fill={fill}
        >
          {label}: {value} ({percent}%)
        </text>
      </g>
    );
  };

  return (
    <>
      <div className="flex flex-wrap -m-1">
        {chartData.format?.map((item, index) => (
          <div
            key={item[moduleData.levelColumns.key]}
            className={classNames(
              "flex align-items-center hover:text-gray-500 cursor-pointer m-1 mr-2",
              {
                "opacity-50": !showFilter?.[item[moduleData.levelColumns.key]],
              }
            )}
            onClick={() =>
              setShowFilter((state) => ({
                ...state,
                [item[moduleData.levelColumns.key]]:
                  !state[item[moduleData.levelColumns.key]],
              }))
            }
          >
            <div
              className="flex-shrink-0 w-1rem h-1rem mr-1"
              style={{ backgroundColor: option_colData[index] }}
            ></div>
            <div>{item[moduleData.levelColumns.key]}</div>
          </div>
        ))}
      </div>

      <div className="h-20rem mt-2">
        <ResponsiveContainer height="100%">
          <PieChart width="100%" height="100%">
            <Pie
              data={finalData}
              cx="50%"
              cy="50%"
              // labelLine={false}
              // label={renderCustomizedLabel}
              label={renderActiveShape}
              // label
              startAngle={-270}
              endAngle={-630}
              innerRadius="0%"
              outerRadius="80%"
              fill="#8884d8"
              dataKey="value"
              onClick={(e) => clickEvent(e)}
            >
              {finalData?.map((item, index) => (
                <Cell
                  key={`cell_${item.name}_${item.value}`}
                  fill={option_colData[index]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

export default useChartPie;
